import { Button, Card, Dropdown, Modal, Pagination, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isArrayNonEmpty } from "../../util/ApiHelper";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { useDispatch, useSelector } from "react-redux";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";
import { formatDate } from "../../util/helper";
import RFQStatusTags from "../requestQuotation/RFQStatus";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileMarkdownOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import ListEmptyComponent from "../../components/ListEmptyComponent/ListEmptyComponent";
import { deleteRFQDataAction } from "../../appRedux/actions/requestQuotationAction";
import { useHistory } from "react-router-dom";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { PriceCalculatorItemMobile } from "./PriceCalculatorItemMobile";
import { navigateToDownloadDetails } from "./NavigateToDownloadDetail";


const PriceCalculatorList = () => {
  const [priceCalculatorList, setPriceCalculatorList] = useState(null);
  const { isMobile } = useGetBreakpoints();
  const [pageSize, setPageSize] = useState(10);
  const [deleteItem, setDeleteItem] = useState(null);
  const dispatch = useDispatch();
  const { loading } = useSelector(({ common }) => common);
  const history = useHistory();
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const getPriceCalculations = useCallback(async () => {
    try {
      dispatch(fetchStart());
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/pricecalculation`
      );
      setPriceCalculatorList({
        rows: response.data,
        count: response.data.length,
      });
      dispatch(fetchSuccess());
    } catch (error) {
      console.error("Error fetching data:", error);
      dispatch(fetchSuccess());
    }
  }, [dispatch]);
  useEffect(() => {
    getPriceCalculations();
  }, [getPriceCalculations]);

  const showDeleteItemModel = useCallback((id) => {
    setDeleteItem(id);
    setShowDeleteModel(true);
  }, []);

  const navigateToDetails = useCallback(
    async (calculationId, isClone = false) => {
      try {
        if (calculationId) {
          if (isClone) {
            history.push(`price-calculator/details`, {
              cloneId: calculationId,
            });
          } else {
            history.push(`price-calculator/details/${calculationId}`);
          }
        } else {
          history.push(`price-calculator/details`);
        }
      } catch (error) {
        console.log("Error navigating:", error);
      } finally {
        dispatch(fetchSuccess());
      }
    },
    [history]
  );

  const handleDownloadDetails = useCallback((calculationId) => {
    navigateToDownloadDetails(calculationId, dispatch, setIsGeneratingPDF)
  },[dispatch])

  const handleCancelModel = () => {
    setShowDeleteModel(false);
    setDeleteItem(null);
  };

  const handleDelete = useCallback(async () => {
    try {
      dispatch(fetchStart());
      await axiosInstance.delete(
        `${BASE_API_CONFIG.baseURL}/pricecalculation/${deleteItem}`
      );

      await getPriceCalculations();

      setShowDeleteModel(false);
      setDeleteItem(null);
      dispatch(fetchSuccess());
    } catch (error) {
      console.error("Error deleting item:", error);
      dispatch(fetchSuccess());
      setShowDeleteModel(false);
      setDeleteItem(null);
    }
  }, [deleteItem, dispatch, getPriceCalculations]);

  const getPrimaryItemQuantity = (items) => {
    if (!items || !Array.isArray(items)) return 1;
    const primaryItem = items.find((item) => item.isPrimary === true);
    return primaryItem
      ? Number(primaryItem.noOfUnits)
      : items.length > 0
      ? Number(items[0].noOfUnits)
      : 1;
  };

  const moreActionsRenderer = useCallback(
    (record) => (
      <div className="myexim-action-icon" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          menu={{
            items: [
              {
                label: "View Details",
                key: "1",
                icon: <EditOutlined />,
                onClick: () => {
                  navigateToDetails(record?.id);
                },
              },
              {
                label: "Copy",
                key: "2",
                icon: <CopyOutlined />,
                onClick: () => {
                  navigateToDetails(record?.id, true);
                },
              },
              {
                label: "Delete",
                key: "3",
                icon: <DeleteOutlined />,
                onClick: () => {
                  showDeleteItemModel(record?.id);
                },
              },
              {
                label: "Download PDF",
                key: "4",
                icon: <FileMarkdownOutlined />,
                onClick: () => handleDownloadDetails(record.id),
                disabled: isGeneratingPDF,
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreOutlined />
        </Dropdown>
      </div>
    ),
    [handleDownloadDetails, isGeneratingPDF, navigateToDetails, showDeleteItemModel]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Date",
        key: "createdAt",
        render: (record) => <div>{formatDate(record?.createdAt)}</div>,
      },
      {
        title: "Template Name",
        key: "templateName",
        render: (record) => <div>{record?.templateName}</div>,
      },
      {
        title: "Total Price Per Unit(INR)",
        key: "totalPricePerUnit",
        align: "right",
        render: (record) => {
          const primaryItem = record.items.find(
            (item) => item.isPrimary === true
          );
          const noOfUnits = primaryItem ? Number(primaryItem.noOfUnits) || 0 : 0;
          const totalAmount = parseFloat(record?.totalAmount) || 0;
          const unitOfMeasurement = primaryItem
            ? primaryItem.uom
            : record.items.length > 0
            ? record.items[0].uom
            : "";
          const pricePerUnit = noOfUnits > 0 ? totalAmount / noOfUnits : 0;
          return `${pricePerUnit.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} / ${unitOfMeasurement}`;
        },
      },
      {
        title: "Total Price(INR)",
        key: "totalAmount",
        align: "right",
        render: (record) => {
          const amount =
            typeof record?.totalAmount === "string"
              ? parseFloat(record.totalAmount)
              : record?.totalAmount || 0;

          return amount.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
    ];
    const hasForeignCurrency = priceCalculatorList?.rows?.some((record) =>
      record.items?.some((item) => item.currency !== "INR")
    );

    if (hasForeignCurrency) {
      baseColumns.splice(2, 0, {
        title: "Total Price Per Unit(Foreign Currency)",
        key: "totalPricePerUnitForeign",
        align: "right",
        render: (record) => {
          const foreignItem = record.items.find(
            (item) => item.currency !== "INR"
          );
          if (!foreignItem) return "-";

          const primaryItem = record?.items.find(
            (item) => item.isPrimary === true
          );
          const noOfUnits = primaryItem ? Number(primaryItem.noOfUnits) || 0 : 0;

          const unitOfMeasurement = primaryItem
            ? primaryItem.uom
            : record.items.length > 0
            ? record.items[0].uom
            : "";
          const exchangeRate = parseFloat(foreignItem?.exchangeRate);
          const totalAmount = parseFloat(record?.totalAmount);
          const totalAmountForeign = totalAmount / exchangeRate;
          const pricePerUnit =
            noOfUnits > 0 ? totalAmountForeign / noOfUnits : 0;

          return `${foreignItem.currency} ${pricePerUnit.toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )} / ${unitOfMeasurement}`;
        },
      });

      baseColumns.splice(4, 0, {
        title: "Total Price(Foreign Currency)",
        key: "totalPriceForeign",
        align: "right",
        render: (record) => {
          const foreignItem = record.items.find(
            (item) => item.currency !== "INR"
          );
          if (!foreignItem) return "-";
          const exchangeRate = parseFloat(foreignItem?.exchangeRate) || 1;
          const totalAmount = parseFloat(record?.totalAmount) || 0;
          const totalAmountForeign = totalAmount / exchangeRate;

          return `${foreignItem.currency} ${totalAmountForeign.toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`;
        },
      });
    }

    baseColumns.push({
      title: "Actions",
      key: "actions",
      render: moreActionsRenderer,
    });

    return baseColumns;
  }, [moreActionsRenderer, priceCalculatorList?.rows]);

  const onShowSizeChange = useCallback(
    (current, pageSize) => {
      setPageSize(pageSize);
    },
    [dispatch]
  );

  const renderContent = useCallback(
    (data, count) => {
      return isMobile ? (
        <>
        <Card bodyStyle={{ padding: 0 }}>
          {data.map((record) => (
            <PriceCalculatorItemMobile
              key={record.id}
              postedDate={formatDate(record.createdAt)}
              templateName={record.templateName}
              totalAmount={record.totalAmount}
              items={record.items}
              MoreActions={() => moreActionsRenderer(record)}
              handleNavigation={() => navigateToDetails(record.id)}
            />
          ))}
        </Card>
        <Pagination
          showSizeChanger
          pageSize={pageSize}
          total={count}
          onChange={onShowSizeChange}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          style={{ marginTop: 16, textAlign: "right" }}
        />
        </>
      ) : (
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          className="gx-table-responsive rfq-table myexim-table"
          columns={columns}
          dataSource={data}
          pagination={{
            position: ["bottomRight"],
            showSizeChanger: true,
            pageSize: pageSize,
            total: count,
            onChange: onShowSizeChange,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onRow={(record) => {
            return {
              onClick: (e) => {
                if (
                  !e.target.classList.contains(
                    "ant-dropdown-menu-title-content"
                  )
                ) {
                  navigateToDetails(record?.id);
                }
              },
            };
          }}
        />
      );
    },
    [columns, isMobile, navigateToDetails, onShowSizeChange, pageSize]
  );

  return (
    <>
      <Card
        style={{ width: "100%" }}
        title={
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div className="myexim-table-title">{"Price Calculator"}</div>

              {isArrayNonEmpty(priceCalculatorList?.rows) && (
                <Button type="primary" onClick={() => navigateToDetails()}>
                  {isMobile ? "+" : "+ Create New"}
                </Button>
              )}
            </div>
          </div>
        }
      >
        {isArrayNonEmpty(priceCalculatorList?.rows)
          ? renderContent(priceCalculatorList?.rows, priceCalculatorList?.count)
          : !loading && (
              <ListEmptyComponent
                message="No Price Calculate"
                buttonText="+ Create New"
                onButtonClick={() => navigateToDetails()}
              />
            )}
      </Card>
      <Modal
        title="Confirmation"
        open={showDeleteModel}
        onCancel={handleCancelModel}
        centered
        footer={null}
      >
        <div className={"delete-modal-content"}>
          <ExclamationCircleOutlined className={"delete-icon"} size={40} />{" "}
          <p>Do you want to delete this item?</p>
          <Button type="default" onClick={() => handleCancelModel()}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => handleDelete()}>
            Ok
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default PriceCalculatorList;
